<script setup lang="ts">
// WTA-"Unlocked"-style join band shown before the footer on public pages (guests
// only): a rounded aubergine panel with a left intro + CTAs and a right green
// "CPA UNLOCKED" card over a pickleball photo. Mirrors wtatennis.com's join CTA.
import heroImage from '@/assets/hero-pickleball.jpg'

const photoStyle = {
  backgroundImage: `linear-gradient(90deg, rgba(0,140,78,0.05) 0%, rgba(0,140,78,0.55) 78%, rgba(0,163,89,0.9) 100%), url(${heroImage})`,
}
</script>

<template>
  <section class="join">
    <div class="join__panel">
      <!-- Left: intro + CTAs -->
      <div class="join__intro">
        <h2 class="join__headline cpa-display">Unlock the Caraga league today</h2>
        <p class="join__sub">
          Join for FREE to follow live rankings, book courts, track your ELO, and
          compete across Region XIII. Game on, players.
        </p>
        <div class="join__actions">
          <v-btn
            to="/register"
            color="secondary"
            size="large"
            variant="flat"
            class="join__cta"
            prepend-icon="mdi-account-plus"
          >
            Join for free
          </v-btn>
          <RouterLink to="/login" class="join__more">
            Sign in
            <v-icon icon="mdi-arrow-right" size="18" />
          </RouterLink>
        </div>
      </div>

      <!-- Right: "CPA UNLOCKED" card -->
      <RouterLink to="/register" class="join__card" :style="photoStyle">
        <div class="join__card-body">
          <div class="join__card-brand">
            <span class="cpa-display join__card-logo">CPA</span>
            <span class="join__card-unlocked">UNLOCKED</span>
          </div>
          <div class="join__card-title cpa-display">
            Unlock your pickleball experience
          </div>
          <p class="join__card-copy">
            Create a free account to get rankings, court bookings, and live
            competition across Caraga.
          </p>
        </div>
      </RouterLink>
    </div>
  </section>
</template>

<style scoped>
.join {
  padding: 8px 16px 40px;
}
.join__panel {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  align-items: stretch;
  gap: 36px;
  background: linear-gradient(135deg, var(--cpa-primary-900) 0%, var(--cpa-primary-800) 60%, #3c1457 130%);
  border-radius: var(--cpa-radius-xl);
  padding: 44px 48px;
  box-shadow: var(--cpa-shadow-lg);
  overflow: hidden;
}

/* Left intro */
.join__intro {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
}
.join__headline {
  font-size: clamp(2rem, 3.6vw, 3rem);
  line-height: 0.96;
  color: var(--cpa-green-bright);
  margin: 0 0 16px;
}
.join__sub {
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.02rem;
  line-height: 1.55;
  max-width: 30rem;
  margin: 0 0 26px;
}
.join__actions {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.join__cta {
  font-weight: 800;
}
.join__more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: gap var(--cpa-dur) var(--cpa-ease), color var(--cpa-dur) var(--cpa-ease);
}
.join__more:hover {
  gap: 9px;
  color: var(--cpa-green-bright);
}

/* Right card */
.join__card {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 220px;
  border-radius: var(--cpa-radius-lg);
  background-color: var(--cpa-secondary-600);
  background-size: cover;
  background-position: center;
  text-decoration: none;
  overflow: hidden;
  transition: transform var(--cpa-dur) var(--cpa-ease), box-shadow var(--cpa-dur) var(--cpa-ease);
}
.join__card:hover {
  transform: translateY(-3px);
  box-shadow: var(--cpa-shadow-lg);
}
.join__card-body {
  margin-left: auto;
  width: 60%;
  padding: 26px 28px;
  color: #fff;
}
.join__card-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.join__card-logo {
  font-style: italic;
  font-weight: 800;
  font-size: 1.4rem;
  letter-spacing: -0.02em;
  color: var(--cpa-primary-900);
}
.join__card-unlocked {
  font-family: var(--cpa-font-heading);
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.06em;
  color: var(--cpa-primary-900);
}
.join__card-title {
  font-size: clamp(1.5rem, 2.4vw, 2.1rem);
  line-height: 0.98;
  color: #fff;
  margin-bottom: 12px;
  text-shadow: 0 2px 12px rgba(10, 62, 38, 0.45);
}
.join__card-copy {
  font-size: 0.86rem;
  line-height: 1.45;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
  text-shadow: 0 1px 8px rgba(10, 62, 38, 0.4);
}

@media (max-width: 900px) {
  .join__panel {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 34px 28px;
  }
  .join__card {
    min-height: 200px;
  }
  .join__card-body {
    width: 66%;
  }
}
@media (max-width: 560px) {
  .join__card-body {
    width: 100%;
    background: rgba(8, 60, 42, 0.32);
  }
}
</style>
